/**
 * 顧客詳細「商談」タブ (2026-10 再設計)。
 *
 * 左: 商談リスト (状態・主軸カテゴリー・金額・最終更新)。選んだ商談の内容を右にまとめる:
 *  - 上部: タイトル / 状態 / 主軸カテゴリー / 金額 / 「商談詳細を開く」/ 工程ステッパー
 *          担当者（社内）・SA ⇄ 紹介者（弊社紹介）・親（所有者）を表示・変更 (DealRelationsBar、商談詳細ヘッダーと共用)
 *  - 書類: 発行した書類 (DealIssuedDocuments) + 受け取った書類 (DealReceivedDocuments)
 *  - メモ: Deal.memo を表示・保存 (商談詳細「概要」と同じ値)
 *  - 商談ログ: activities を「誰が (自動 / 担当者名)」付きで表示
 *
 * 2026-10-30: 選んだ商談を URL (?deal=) に持たせ、商談詳細を開く時は戻り先 (顧客詳細) を渡す。
 *   商談詳細の「前の画面へ」で、このタブ・この商談を選んだ状態に戻れる。
 */
import type { DealActivityType, IsoOrTimestamp } from '@goenhub/shared';
import { ArrowRight, Inbox, Plus } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { DealIssuedDocuments } from '../components/DealIssuedDocuments';
import { DealReceivedDocuments } from '../components/DealReceivedDocuments';
import { DealStageBadge } from '../components/DealStageBadge';
import { CreateDealDialog } from '../dialogs/CreateDealDialog';

import { EmptyState } from '@/components/common/EmptyState';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { useAdminDirectory, type AdminDirectoryEntry } from '@/hooks/useAdmins';
import { useDealConsentRequirement } from '@/hooks/useConsentDocuments';
import { useDealActivities, useDeals, useUpdateDeal, type DealRecord } from '@/hooks/useDeals';
import { useMailContact } from '@/hooks/useMailContacts';
import { useProductCategories } from '@/hooks/useProductCategories';
import { useReturnToState } from '@/hooks/useReturnTo';
import { useUrlParam } from '@/hooks/useUrlParam';
import { DealRelationsBar } from '@/pages/deals/components/DealRelationsBar';
import { DealStageStepper } from '@/pages/deals/components/DealStageStepper';
import { DocumentMailDialog } from '@/pages/deals/components/DocumentMailDialog';
import { CreateQuoteDialog } from '@/pages/deals/dialogs/CreateQuoteDialog';
import { paths } from '@/routes/paths';

interface Props {
  contactId: string;
}

type NameOf = (uid: string | null | undefined) => string;

export function ContactDealsTab({ contactId }: Props) {
  const dealsQuery = useDeals({ contactId });
  const contact = useMailContact(contactId);
  const { data: categories } = useProductCategories({ includeInactive: true });
  const directory = useAdminDirectory();
  const deals = dealsQuery.data ?? [];

  // URL の ?deal= の商談。無い・見つからない (削除された) 時は最新の商談を選ぶ
  const [urlDealId, setSelectedDealId] = useUrlParam('deal');
  const selectedDealId =
    urlDealId && deals.some((d) => d.id === urlDealId) ? urlDealId : (deals[0]?.id ?? null);
  const [createQuoteOpen, setCreateQuoteOpen] = useState(false);
  const [createDealOpen, setCreateDealOpen] = useState(false);
  // 「保存して送信」で作成した下書きのメール送信モーダル対象 (DealQuotesTab と同じ導線)
  const [mailQuoteId, setMailQuoteId] = useState<string | null>(null);

  const nameOf = useMemo<NameOf>(() => {
    const m = new Map<string, AdminDirectoryEntry>();
    for (const a of directory.data ?? []) m.set(a.uid, a);
    return (uid) => {
      if (!uid) return '';
      const a = m.get(uid);
      return a ? a.fullName || a.displayName || a.email : '';
    };
  }, [directory.data]);

  const categoryOf = (d: DealRecord) =>
    (categories ?? []).find((c) => c.id === d.primaryCategoryId) ?? null;

  const selectedDeal = deals.find((d) => d.id === selectedDealId) ?? null;
  const defaultDealTitle = contact.data?.name ? `${contact.data.name} 様 / 新規商談` : '新規商談';

  return (
    <>
      <p className="text-[13px] text-[var(--text-secondary)] mb-3">
        商談を選ぶと、その商談の状況・関係者・書類・メモ・ログがここにまとまります。
      </p>
      {dealsQuery.isLoading ? (
        <Spinner />
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-[300px_minmax(0,1fr)] gap-4 items-start">
          <DealsList
            deals={deals}
            selectedDealId={selectedDealId}
            categoryName={(d) => categoryOf(d)?.name ?? d.primaryCategory ?? '未設定'}
            onSelect={setSelectedDealId}
            onCreate={() => setCreateDealOpen(true)}
          />
          {selectedDeal ? (
            <SelectedDeal
              key={selectedDeal.id}
              deal={selectedDeal}
              categoryName={
                categoryOf(selectedDeal)?.name ?? selectedDeal.primaryCategory ?? '未設定'
              }
              isTimeWaver={categoryOf(selectedDeal)?.name === 'TimeWaver'}
              currency={categoryOf(selectedDeal)?.manufacturerCurrency || 'EUR'}
              nameOf={nameOf}
              onCreateQuote={() => setCreateQuoteOpen(true)}
            />
          ) : (
            <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-6">
              <EmptyState
                icon={Inbox}
                title="商談がありません"
                description="個別説明申込・見積依頼の自動起票を待つか、「新規商談」から手動で起票できます。"
              />
            </div>
          )}
        </div>
      )}

      {selectedDeal && (
        <CreateQuoteDialog
          open={createQuoteOpen}
          onOpenChange={setCreateQuoteOpen}
          dealId={selectedDeal.id}
          contactId={selectedDeal.contactId}
          defaultRecipientEmail={contact.data?.defaultEmail ?? null}
          defaultTitle={selectedDeal.title ? `${selectedDeal.title} お見積書` : undefined}
          onCreatedForSend={(quoteId) => setMailQuoteId(quoteId)}
        />
      )}

      {selectedDeal && (
        <DocumentMailDialog
          open={mailQuoteId !== null}
          onOpenChange={(o) => !o && setMailQuoteId(null)}
          documentType="quote"
          dealId={selectedDeal.id}
          documentId={mailQuoteId}
        />
      )}

      <CreateDealDialog
        open={createDealOpen}
        onOpenChange={setCreateDealOpen}
        contactId={contactId}
        contactKind={contact.data?.kind}
        defaultTitle={defaultDealTitle}
      />
    </>
  );
}

// ─────────────────────────────────────────────
// 左: 商談リスト
// ─────────────────────────────────────────────

function DealsList({
  deals,
  selectedDealId,
  categoryName,
  onSelect,
  onCreate,
}: {
  deals: DealRecord[];
  selectedDealId: string | null;
  categoryName: (d: DealRecord) => string;
  onSelect: (id: string) => void;
  onCreate: () => void;
}) {
  return (
    <Section
      title="商談リスト"
      subtitle={`${deals.length} 件`}
      action={
        <Button type="button" variant="primary" size="sm" onClick={onCreate}>
          <Plus className="size-3.5 mr-1" />
          新規商談
        </Button>
      }
    >
      {deals.length === 0 ? (
        <p className="text-xs text-[var(--text-muted)]">商談はまだありません。</p>
      ) : (
        <div className="space-y-1.5">
          {deals.map((d) => {
            const isSelected = d.id === selectedDealId;
            return (
              <button
                key={d.id}
                type="button"
                onClick={() => onSelect(d.id)}
                aria-pressed={isSelected}
                className={`w-full text-left px-3 py-2.5 rounded-md border transition-colors space-y-1.5 ${
                  isSelected
                    ? 'border-[var(--accent)] bg-[var(--accent-subtle)]'
                    : 'border-[var(--border)] bg-[var(--bg-surface)] hover:bg-[var(--bg-hover)]'
                }`}
              >
                <div className="text-sm font-medium text-[var(--text-primary)] leading-snug">
                  {d.title || '(タイトル未設定)'}
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  <DealStageBadge stage={d.stage} />
                  <span className="inline-flex items-center h-5 px-1.5 rounded border border-[var(--border)] text-[10px] text-[var(--text-secondary)]">
                    {categoryName(d)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-[11px] text-[var(--text-muted)]">
                  <span className="tabular-nums">
                    {formatYen(d.finalAmount ?? d.expectedAmount)}
                  </span>
                  <span>{formatRelative(d.lastTouchedAt)}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </Section>
  );
}

// ─────────────────────────────────────────────
// 右: 選択中の商談
// ─────────────────────────────────────────────

function SelectedDeal({
  deal,
  categoryName,
  isTimeWaver,
  currency,
  nameOf,
  onCreateQuote,
}: {
  deal: DealRecord;
  categoryName: string;
  isTimeWaver: boolean;
  currency: string;
  nameOf: NameOf;
  onCreateQuote: () => void;
}) {
  const navigate = useNavigate();
  const returnToState = useReturnToState('顧客詳細');
  // 同意ステップの要否 (主軸カテゴリーの設定 + 同じカテゴリーの別の商談でのサイン有無)
  const { requiresConsent } = useDealConsentRequirement(deal);

  return (
    <div className="space-y-4 min-w-0">
      <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-4 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 space-y-1.5">
            <div className="text-[11px] text-[var(--text-muted)]">選択中の商談</div>
            <div className="text-base font-semibold text-[var(--text-primary)] leading-snug">
              {deal.title || '(タイトル未設定)'}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <DealStageBadge stage={deal.stage} />
              <span className="inline-flex items-center h-5 px-1.5 rounded border border-[var(--border)] text-[10px] text-[var(--text-secondary)]">
                主軸カテゴリー: {categoryName}
              </span>
              <span className="text-sm font-semibold tabular-nums">
                {formatYen(deal.finalAmount ?? deal.expectedAmount)}
              </span>
            </div>
          </div>
          <Button
            variant="primary"
            size="sm"
            onClick={() =>
              navigate(paths.dealDetail.replace(':id', deal.id), { state: returnToState })
            }
          >
            商談詳細を開く
            <ArrowRight className="size-3.5" />
          </Button>
        </div>
        <DealStageStepper deal={deal} requiresConsent={requiresConsent} />
        <DealRelationsBar deal={deal} />
      </div>

      <div className="grid grid-cols-1 2xl:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)] gap-4 items-start">
        <Section title="書類">
          <div className="space-y-5">
            <DealIssuedDocuments
              dealId={deal.id}
              currency={currency}
              onCreateQuote={onCreateQuote}
            />
            <DealReceivedDocuments
              dealId={deal.id}
              isTimeWaver={isTimeWaver}
              currency={currency}
              nameOf={nameOf}
            />
          </div>
        </Section>
        <div className="space-y-4 min-w-0">
          <DealMemo deal={deal} />
          <DealLog dealId={deal.id} nameOf={nameOf} />
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// メモ (Deal.memo。商談詳細「概要」のメモと同じ値)
// ─────────────────────────────────────────────

function DealMemo({ deal }: { deal: DealRecord }) {
  const update = useUpdateDeal();
  const [memo, setMemo] = useState(deal.memo ?? '');

  useEffect(() => {
    setMemo(deal.memo ?? '');
  }, [deal.memo]);

  const dirty = memo !== (deal.memo ?? '');

  const handleSave = async () => {
    try {
      await update.mutateAsync({ id: deal.id, memo });
      toast.success('メモを保存しました');
    } catch (err) {
      toast.error(`保存失敗: ${(err as Error).message}`);
    }
  };

  return (
    <Section title="メモ">
      <div className="space-y-2">
        <Textarea
          value={memo}
          onChange={(e) => setMemo(e.target.value)}
          rows={4}
          placeholder="この商談のメモ（商談詳細の「概要」と同じ内容）"
          aria-label="商談メモ"
          className="text-xs"
        />
        <div className="flex justify-end">
          <Button
            variant="primary"
            size="sm"
            onClick={() => void handleSave()}
            disabled={!dirty || update.isPending}
          >
            {update.isPending ? '保存中…' : dirty ? '保存' : '保存済'}
          </Button>
        </div>
      </div>
    </Section>
  );
}

// ─────────────────────────────────────────────
// 商談ログ (誰が行ったか: 自動 / AI / 担当者名)
// ─────────────────────────────────────────────

function DealLog({ dealId, nameOf }: { dealId: string; nameOf: NameOf }) {
  const activities = useDealActivities(dealId);

  const actor = (createdBy: string | undefined): { text: string; system: boolean } => {
    if (!createdBy || createdBy === 'system') return { text: '自動', system: true };
    if (createdBy === 'ai') return { text: 'AI', system: true };
    return { text: nameOf(createdBy) || '担当者', system: false };
  };

  return (
    <Section title="商談ログ">
      {activities.isLoading ? (
        <Spinner />
      ) : !activities.data || activities.data.length === 0 ? (
        <p className="text-xs text-[var(--text-muted)]">
          ログはまだありません。商談が動くと自動で記録されます。
        </p>
      ) : (
        <div className="space-y-1.5 max-h-[520px] overflow-y-auto pr-1">
          {activities.data.map((a) => {
            const who = actor(a.createdBy);
            const typeLabel = a.type === 'manual' ? '' : (ACTIVITY_TYPE_LABEL[a.type] ?? a.type);
            return (
              <div
                key={a.id}
                className="px-3 py-2 rounded-md border border-[var(--border)] bg-[var(--bg-elevated)]"
              >
                <div className="flex flex-wrap items-center gap-1.5 mb-0.5">
                  <span className="text-[10px] text-[var(--text-muted)] tabular-nums">
                    {formatTime(a.createdAt)}
                  </span>
                  <Badge variant={who.system ? 'default' : 'accent'}>{who.text}</Badge>
                  {typeLabel && (
                    <span className="text-[10px] font-semibold text-[var(--text-secondary)]">
                      {typeLabel}
                    </span>
                  )}
                </div>
                <div className="text-xs text-[var(--text-primary)] leading-relaxed">{a.detail}</div>
              </div>
            );
          })}
        </div>
      )}
    </Section>
  );
}

// ─────────────────────────────────────────────
// 共通
// ─────────────────────────────────────────────

const ACTIVITY_TYPE_LABEL: Partial<Record<DealActivityType, string>> = {
  form_submitted: 'フォーム送信',
  mail_sent: 'メール送信',
  meeting_held: '打合せ',
  merged_in: 'マージ受入',
  merged_out: 'マージ送出',
  split_off: '分離',
  stage_changed: 'ステージ変更',
  won_confirmed: '成約',
  lost_confirmed: '失注',
  memo: 'メモ',
};

function Section({
  title,
  subtitle,
  action,
  children,
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-3">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wider">
            {title}
          </h3>
          {subtitle && <p className="text-[10px] text-[var(--text-muted)] mt-0.5">{subtitle}</p>}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function formatYen(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return `¥${v.toLocaleString('ja-JP')}`;
}

function tsToDate(v: IsoOrTimestamp | null | undefined): Date | null {
  if (!v) return null;
  if (typeof v === 'string') {
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof v === 'object' && 'toDate' in v) {
    try {
      return (v as { toDate: () => Date }).toDate();
    } catch {
      return null;
    }
  }
  return null;
}

function formatTime(v: IsoOrTimestamp | null | undefined): string {
  const d = tsToDate(v);
  if (!d) return '';
  return d.toLocaleString('ja-JP', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatRelative(v: IsoOrTimestamp | null | undefined): string {
  const d = tsToDate(v);
  if (!d) return '';
  const diffMs = Date.now() - d.getTime();
  const days = Math.floor(diffMs / (24 * 60 * 60 * 1000));
  if (days === 0) return '今日';
  if (days === 1) return '1日前';
  if (days < 30) return `${days}日前`;
  if (days < 365) return `${Math.floor(days / 30)}ヶ月前`;
  return `${Math.floor(days / 365)}年前`;
}
